import React, { useState } from 'react';
import { Filter, X, Clock, Car, Tag, Radio, User, ArrowUpDown, ArrowUp, ArrowDown, Eye, Moon, Sun, Settings, Search, BookOpen, Lightbulb } from 'lucide-react';
import SystemClock from './SystemClock';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../AuthContext';

const TopBar = ({
  timeFilter,
  setTimeFilter,
  TIME_FILTERS,
  showTime,
  setShowTime,
  showCar,
  setShowCar,
  showChannel,
  setShowChannel,
  showPerson,
  setShowPerson,
  branding,
  isDarkMode,
  timezone,
  setTimezone,
  isMobile,
  edgeServerEndpoint,
  userRole,
  showFullTimestamps,
  setShowFullTimestamps,
  onFilterClick,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  toggleTheme,
  timeFormat = "24h",
  searchQuery,
  setSearchQuery,
  onDocumentationClick,
  isAlertsVisible,
  setIsAlertsVisible,
  isSidebarVisible,
  setIsSidebarVisible,
  sidebarWidth = 0,
  messagesWidth = 0,
  alertsWidth = 0,
  windowWidth = 0,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions(edgeServerEndpoint);
  
  // Show settings button only if user has access_settings permission or is admin
  const canAccessSettings = user?.role === 'admin' || hasPermission('access_settings');
  
  // Helper function to format time display based on time format preference
  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return '';
    if (timeFormat === '24h') return timeStr; // Already in 24h format
    
    // Convert 24h format (HH:MM:SS) to 12h format (HH:MM:SS AM/PM)
    const [hours, minutes, seconds] = timeStr.split(':');
    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 % 12 || 12;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const secs = seconds || '00';
    return `${String(hour12).padStart(2, '0')}:${minutes}:${secs} ${ampm}`;
  };
  const [isViewSettingsOpen, setIsViewSettingsOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isViewModalClosing, setIsViewModalClosing] = useState(false);
  const [isFilterModalClosing, setIsFilterModalClosing] = useState(false);
//  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const closeViewModal = () => {
    setIsViewModalClosing(true);
    setTimeout(() => {
      setIsViewSettingsOpen(false);
      setIsViewModalClosing(false);
    }, 300);
  };

  const closeFilterModal = () => {
    setIsFilterModalClosing(true);
    setTimeout(() => {
      setIsFilterOpen(false);
      setIsFilterModalClosing(false);
    }, 300);
  };

  //  const toggleMultiSelectMode = () => {
  //   setIsMultiSelectMode(!isMultiSelectMode);
  //   setSelectedMessages(new Set());
  // };
  const commonStyles = {
    modalBackground: isDarkMode ? 'bg-gray-900' : 'bg-white',
    borderColor: isDarkMode ? 'border-gray-700' : 'border-gray-200',
    borderColorValue: isDarkMode ? '#374151' : '#e5e7eb', // gray-700 in dark, gray-200 in light
    textColor: isDarkMode ? 'text-white' : 'text-gray-800',
    secondaryTextColor: isDarkMode ? 'text-gray-400' : 'text-gray-500',
    buttonBackground: isDarkMode ? 'bg-gray-800' : 'bg-gray-100',
    buttonHoverBackground: isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200',
    toggleInactive: isDarkMode ? 'bg-gray-700' : 'bg-gray-200',
    inputBackground: isDarkMode ? 'bg-gray-800' : 'bg-gray-50',
    // Static colors for better visibility in dark mode
    headerColor: isDarkMode ? '#ffffff' : '#1f2937', // White in dark, dark gray in light
    iconColor: isDarkMode ? '#3b82f6' : '#2563eb', // Blue shades for icons
    accentColor: isDarkMode ? '#60a5fa' : '#3b82f6', // Lighter blue for accents
  };


  // Calculate section widths for desktop layout
  // Sections are always visible - sidebarWidth and alertsWidth already include minimums when panels are hidden
  const getSectionWidths = () => {
    if (isMobile || windowWidth === 0) {
      return { channels: 0, messages: '100%', insights: 0 };
    }
    
    const totalWidth = windowWidth;
    // Use the provided widths (which already account for minimums when panels are hidden)
    const channelsWidth = sidebarWidth || 250; // Fallback to minimum if somehow 0
    const insightsWidth = alertsWidth || 200; // Fallback to minimum if somehow 0
    const messagesWidthCalc = messagesWidth > 0 ? messagesWidth : (totalWidth - channelsWidth - insightsWidth);
    
    return {
      channels: channelsWidth,
      messages: Math.max(0, messagesWidthCalc),
      insights: insightsWidth
    };
  };

  const sectionWidths = getSectionWidths();

  return (
    <>
     <div className={`sticky top-0 z-20 w-full ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} border-b ${commonStyles.borderColor} shadow-sm transition-colors duration-300`}>
        {isMobile ? (
          // Mobile layout - original compact layout
          <div className={`${isMobile ? 'px-2' : 'px-4 sm:px-6'}`}>
            <div className="flex justify-between items-center h-16">
              {/* Title on the left */}
              <div className="flex flex-col">
                <h2 className={`text-lg font-semibold ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-800'
                }`}>
                  {branding?.organizationName || 'Boondock Aero'}
                </h2>
                <p className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Boondock Technologies
                </p>
              </div>

              {/* Controls on the right */}
              <div className="flex items-center space-x-2">
                {/* Mobile controls - same as before */}
                {isMobile && (
                  <div className="relative flex items-center">
                    <Search
                      className={`absolute left-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      size={16}
                      strokeWidth={2}
                    />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery || ''}
                      onChange={(e) => setSearchQuery && setSearchQuery(e.target.value)}
                      className={`w-32 pl-8 pr-2 py-1.5 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                          : "bg-gray-100 border border-gray-200 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>
                )}
                {/* Other mobile controls... */}
              </div>
            </div>
          </div>
        ) : (
          // Desktop layout - three sections aligned with panels
          <div className="flex h-16">
            {/* Channels Section - Left - Always visible */}
            <div 
              className="flex items-center px-4 border-r transition-all duration-300 flex-shrink-0"
              style={{ 
                width: `${sectionWidths.channels}px`,
                borderColor: commonStyles.borderColorValue
              }}
            >
              <div className="flex items-center gap-3 w-full">
                {/* Title */}
                <div className="flex flex-col flex-shrink-0">
                  <h2 className={`text-lg font-semibold ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-800'
                  }`}>
                    {branding?.organizationName || 'Boondock Aero'}
                  </h2>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Boondock Technologies
                  </p>
                </div>
                
                {/* No icons in Channels section - moved to center Messages section */}
              </div>
            </div>

            {/* Messages Section - Center */}
            <div 
              className="flex items-center px-4 border-r transition-all duration-300 flex-1 min-w-0"
              style={{ 
                width: sectionWidths.messages > 0 ? `${sectionWidths.messages}px` : 'auto',
                borderColor: commonStyles.borderColorValue
              }}
            >
              <div className="flex items-center gap-3 w-full">
                {/* Search Messages Input */}
                <div className="relative flex items-center flex-1">
                  <Search
                    className={`absolute left-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    size={18}
                    strokeWidth={2}
                  />
                  <input
                    type="text"
                    placeholder="Search messages..."
                    value={searchQuery || ''}
                    onChange={(e) => setSearchQuery && setSearchQuery(e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                        : "bg-gray-100 border border-gray-200 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
                
                {/* Icons for Messages section */}
                <div className="flex items-center gap-2">
                  {onFilterClick && (
                    <button
                      onClick={() => setIsFilterOpen(true)}
                      className={`relative group inline-flex items-center justify-center h-12 w-12 rounded-full ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} transition-all duration-300`}
                      aria-label="Filter"
                      title="Filter"
                    >
                      <Filter 
                        className="h-4.5 w-4.5 group-hover:scale-110 transition-all duration-300"
                        style={{ color: commonStyles.iconColor }}
                      />
                    </button>
                  )}
                  <button
                    onClick={() => setIsViewSettingsOpen(true)}
                    className={`relative group inline-flex items-center justify-center h-12 w-12 rounded-full ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} transition-all duration-300`}
                    aria-label="View Settings"
                    title="View Settings"
                  >
                    <Eye 
                      className="h-4.5 w-4.5 group-hover:scale-110 transition-all duration-300"
                      style={{ color: commonStyles.iconColor }}
                    />
                  </button>
                  {canAccessSettings && (
                    <button
                      onClick={() => navigate("/settings")}
                      className={`relative group inline-flex items-center justify-center h-12 w-12 rounded-full ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} transition-all duration-300`}
                      aria-label="Settings"
                      title="Settings"
                    >
                      <Settings 
                        className="h-4.5 w-4.5 group-hover:scale-110 transition-all duration-300"
                        style={{ color: commonStyles.iconColor }}
                      />
                    </button>
                  )}
                  {setIsSidebarVisible !== undefined && (
                    <button
                      onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                      className={`relative group inline-flex items-center justify-center h-12 w-12 rounded-full ${isSidebarVisible ? 'bg-blue-600' : commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} transition-all duration-300`}
                      aria-label="Toggle Sidebar"
                      title={isSidebarVisible ? "Hide Channels" : "Show Channels"}
                    >
                      <Radio 
                        className="h-4.5 w-4.5 group-hover:scale-110 transition-all duration-300"
                        style={{ color: isSidebarVisible ? '#ffffff' : commonStyles.iconColor }}
                      />
                    </button>
                  )}
                  {setIsAlertsVisible !== undefined && (
                    <button
                      onClick={() => setIsAlertsVisible(!isAlertsVisible)}
                      className={`relative group inline-flex items-center justify-center h-12 w-12 rounded-full ${isAlertsVisible ? 'bg-blue-600' : commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} transition-all duration-300`}
                      aria-label="Toggle Insights"
                      title={isAlertsVisible ? "Hide Insights" : "Show Insights"}
                    >
                      <Lightbulb 
                        className="h-4.5 w-4.5 group-hover:scale-110 transition-all duration-300"
                        style={{ color: isAlertsVisible ? '#ffffff' : commonStyles.iconColor }}
                      />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Insights Section - Right - Always visible */}
            <div 
              className="flex items-center px-4 transition-all duration-300 flex-shrink-0"
              style={{ 
                width: `${sectionWidths.insights}px`,
              }}
            >
              <div className="flex items-center gap-3 w-full justify-end">
                {/* Only SystemClock in Insights section - toggle moved to center */}
                <SystemClock
                  edgeServerEndpoint={edgeServerEndpoint}
                  userRole={userRole}
                  isDarkMode={isDarkMode}
                  timeFormat={timeFormat}
                  timezone={timezone}
                  setTimezone={setTimezone}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Settings Modal */}
      {isViewSettingsOpen && (
        <div 
          className={`fixed inset-0 ${isDarkMode ? 'bg-gray-900/80' : 'bg-gray-800/30'} backdrop-blur-md z-30 flex items-center justify-center transitionuffed transition-opacity duration-300 ${isViewModalClosing ? 'opacity-0' : 'opacity-100'}`}
        >
          <div 
            className={`${commonStyles.modalBackground} rounded-2xl w-full max-w-[90%] md:max-w-md mx-4 shadow-2xl relative border ${commonStyles.borderColor} overflow-hidden transition-transform duration-300 ${isViewModalClosing ? 'scale-95' : 'scale-100'}`}
          >
            <div 
              className="absolute top-0 left-0 w-full h-1 transition-colors duration-300"
              style={{ backgroundColor: commonStyles.accentColor }}
            />

            <div className={`relative flex items-center justify-between p-3 md:p-4 border-b ${commonStyles.borderColor}`}>
              <h3 className={`text-base md:text-lg font-bold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                VIEW SETTINGS
              </h3>
              <button
                onClick={closeViewModal}
                className={`p-1 md:p-1.5 rounded-full ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} transition-colors duration-200`}
                aria-label="Close view settings"
              >
                <X className={`h-4 w-4 ${commonStyles.secondaryTextColor}`} />
              </button>
            </div>

            <div className="relative p-4 md:p-6 space-y-4 md:space-y-6">

              <div className="space-y-2 md:space-y-3">
                {[
                  { label: 'TIMING', icon: Clock, state: showTime, setState: setShowTime },
                  { label: 'TAG', icon: Tag, state: showCar, setState: setShowCar },
                  { label: 'CHANNEL', icon: Radio, state: showChannel, setState: setShowChannel },
                  { label: 'PERSON', icon: User, state: showPerson, setState: setShowPerson },
                ].map(({ label, icon: Icon, state, setState }) => (
                  <div 
                    key={label} 
                    className={`flex items-center justify-between p-2 md:p-3 rounded-lg border ${state ? 'border-opacity-50' : 'border-opacity-20'} transition-all duration-300`}
                    style={{ 
                      borderColor: state ? commonStyles.accentColor : commonStyles.borderColor,
                      backgroundColor: state ? commonStyles.accentColor + '10' : (isDarkMode ? 'rgb(31 41 55)' : 'rgb(249 250 251)')
                    }}
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={`p-1 md:p-1.5 rounded-lg ${state ? '' : commonStyles.buttonBackground} transition-all duration-300`}
                        style={{ backgroundColor: state ? commonStyles.accentColor + '30' : '' }}
                      >
                        <Icon 
                          className="h-3.5 w-3.5 transition-all duration-300"
                          style={{ color: commonStyles.accentColor }}
                        />
                      </div>
                      <span className={`text-xs font-semibold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                        {label}
                      </span>
                    </div>
                    <button
                      onClick={() => setState(!state)}
                      className={`relative inline-flex h-4 md:h-5 w-8 md:w-9 items-center rounded-full transition-all duration-300 ease-out focus:outline-none ${
                        state 
                          ? 'bg-blue-600' 
                          : isDarkMode 
                            ? 'bg-gray-600' 
                            : 'bg-gray-300'
                      }`}
                      aria-checked={state}
                      role="switch"
                    >
                      <span 
                        className={`inline-block h-3 md:h-4 w-3 md:w-4 transform rounded-full shadow-md transition-all duration-300 ease-out ${
                          state 
                            ? 'translate-x-4 md:translate-x-4 bg-white' 
                            : isDarkMode 
                              ? 'translate-x-0.5 bg-gray-400' 
                              : 'translate-x-0.5 bg-gray-500'
                        }`} 
                      />
                    </button>
                  </div>
                ))}
              </div>

              {/* Show Full Timestamps Toggle */}
              <div className="space-y-2">
                <label 
                  className={`flex items-center gap-2 text-xs font-semibold tracking-wide transition-colors duration-300`}
                  style={{ color: commonStyles.accentColor }}
                >
                  <Clock className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                  DATE
                </label>
                <div 
                  className={`flex items-center justify-between p-2 md:p-3 rounded-lg border ${showFullTimestamps ? 'border-opacity-50' : 'border-opacity-20'} transition-all duration-300`}
                  style={{ 
                    borderColor: showFullTimestamps ? commonStyles.accentColor : commonStyles.borderColor,
                    backgroundColor: showFullTimestamps ? commonStyles.accentColor + '10' : (isDarkMode ? 'rgb(31 41 55)' : 'rgb(249 250 251)')
                  }}
                >
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className={`p-1 md:p-1.5 rounded-lg ${showFullTimestamps ? '' : commonStyles.buttonBackground} transition-all duration-300`}
                      style={{ backgroundColor: showFullTimestamps ? commonStyles.accentColor + '30' : '' }}
                    >
                      <Clock 
                        className="h-3.5 w-3.5 transition-all duration-300"
                        style={{ color: commonStyles.accentColor }}
                      />
                    </div>
                    <span className={`text-xs font-semibold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                      Show full timestamps
                    </span>
                  </div>
                  <button
                    onClick={() => setShowFullTimestamps(!showFullTimestamps)}
                    className={`relative inline-flex h-4 md:h-5 w-8 md:w-9 items-center rounded-full transition-all duration-300 ease-out focus:outline-none ${
                      showFullTimestamps 
                        ? 'bg-blue-600' 
                        : isDarkMode 
                          ? 'bg-gray-600' 
                          : 'bg-gray-300'
                    }`}
                    aria-checked={showFullTimestamps}
                    role="switch"
                  >
                    <span 
                      className={`inline-block h-3 md:h-4 w-3 md:w-4 transform rounded-full shadow-md transition-all duration-300 ease-out ${
                        showFullTimestamps 
                          ? 'translate-x-4 md:translate-x-4 bg-white' 
                          : isDarkMode 
                            ? 'translate-x-0.5 bg-gray-400' 
                            : 'translate-x-0.5 bg-gray-500'
                      }`} 
                    />
                  </button>
                </div>
              </div>

              {/* Theme Toggle */}
              {toggleTheme && (
                <div className="space-y-2">
                  <label 
                    className={`flex items-center gap-2 text-xs font-semibold tracking-wide transition-colors duration-300`}
                    style={{ color: commonStyles.accentColor }}
                  >
                    {isDarkMode ? (
                      <Sun className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                    ) : (
                      <Moon className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                    )}
                    THEME
                  </label>
                  <div 
                    className={`flex items-center justify-between p-2 md:p-3 rounded-lg border ${isDarkMode ? 'border-opacity-50' : 'border-opacity-20'} transition-all duration-300`}
                    style={{ 
                      borderColor: isDarkMode ? commonStyles.accentColor : commonStyles.borderColor,
                      backgroundColor: isDarkMode ? commonStyles.accentColor + '10' : (isDarkMode ? 'rgb(31 41 55)' : 'rgb(249 250 251)')
                    }}
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={`p-1 md:p-1.5 rounded-lg ${isDarkMode ? '' : commonStyles.buttonBackground} transition-all duration-300`}
                        style={{ backgroundColor: isDarkMode ? commonStyles.accentColor + '30' : '' }}
                      >
                        {isDarkMode ? (
                          <Sun 
                            className="h-3.5 w-3.5 transition-all duration-300"
                            style={{ color: commonStyles.accentColor }}
                          />
                        ) : (
                          <Moon 
                            className="h-3.5 w-3.5 transition-all duration-300"
                            style={{ color: commonStyles.accentColor }}
                          />
                        )}
                      </div>
                      <span className={`text-xs font-semibold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                        {isDarkMode ? 'Dark Mode' : 'Light Mode'}
                      </span>
                    </div>
                    <button
                      onClick={toggleTheme}
                      className={`relative inline-flex h-4 md:h-5 w-8 md:w-9 items-center rounded-full transition-all duration-300 ease-out focus:outline-none ${
                        isDarkMode 
                          ? 'bg-blue-600' 
                          : 'bg-gray-300'
                      }`}
                      aria-checked={isDarkMode}
                      role="switch"
                    >
                      <span 
                        className={`inline-block h-3 md:h-4 w-3 md:w-4 transform rounded-full shadow-md transition-all duration-300 ease-out ${
                          isDarkMode 
                            ? 'translate-x-4 md:translate-x-4 bg-white' 
                            : 'translate-x-0.5 bg-gray-500'
                        }`} 
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className={`relative flex justify-end px-4 md:px-6 py-3 md:py-4 border-t ${commonStyles.borderColor} transition-colors duration-300`}>
              <button
                onClick={closeViewModal}
                className={`px-4 md:px-5 py-1.5 md:py-2 rounded-lg text-sm font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg active:scale-95`}
                style={{ backgroundColor: commonStyles.accentColor, outline: 'none' }}
              >
                APPLY
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {isFilterOpen && (
        <div 
          className={`fixed inset-0 ${isDarkMode ? 'bg-gray-900/80' : 'bg-gray-800/30'} backdrop-blur-md z-30 flex items-center justify-center transitionuffed transition-opacity duration-300 ${isFilterModalClosing ? 'opacity-0' : 'opacity-100'}`}
        >
          <div 
            className={`${commonStyles.modalBackground} rounded-2xl w-full max-w-[90%] md:max-w-md mx-4 shadow-2xl relative border ${commonStyles.borderColor} overflow-hidden transition-transform duration-300 ${isFilterModalClosing ? 'scale-95' : 'scale-100'}`}
          >
            <div 
              className="absolute top-0 left-0 w-full h-1 transition-colors duration-300"
              style={{ backgroundColor: commonStyles.accentColor }}
            />

            <div className={`relative flex items-center justify-between p-3 md:p-4 border-b ${commonStyles.borderColor}`}>
              <h3 className={`text-base md:text-lg font-bold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                FILTER
              </h3>
              <button
                onClick={closeFilterModal}
                className={`p-1 md:p-1.5 rounded-full ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} transition-colors duration-200`}
                aria-label="Close filter"
              >
                <X className={`h-4 w-4 ${commonStyles.secondaryTextColor}`} />
              </button>
            </div>

            <div className="relative p-4 md:p-6 space-y-5 md:space-y-6">
              {/* TIME RANGE */}
              <div className="space-y-2.5">
                <label 
                  className={`flex items-center gap-2 text-xs font-semibold tracking-wide transition-colors duration-300 mb-2`}
                  style={{ color: commonStyles.accentColor }}
                >
                  <Clock className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                  TIME RANGE
                </label>
                <div className={`relative rounded-lg border ${commonStyles.borderColor} transition-colors duration-300`}>
                  <select
                    value={timeFilter}
                    onChange={(e) => setTimeFilter(e.target.value)}
                    className={`w-full appearance-none rounded-lg ${commonStyles.inputBackground} px-3 py-2.5 text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                    style={{ borderColor: commonStyles.accentColor + '40' }}
                  >
                    {Object.values(TIME_FILTERS).map((filter) => (
                      <option key={filter} value={filter} className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        {filter}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <svg className={`h-4 w-4 ${commonStyles.secondaryTextColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Custom Date Range Filter */}
              <div className="space-y-2.5">
                <label 
                  className={`flex items-center gap-2 text-xs font-semibold tracking-wide transition-colors duration-300 mb-2`}
                  style={{ color: commonStyles.accentColor }}
                >
                  <Clock className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                  CUSTOM DATE RANGE
                </label>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={startDate || ''}
                        onChange={(e) => setStartDate(e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-lg border ${commonStyles.borderColor} ${commonStyles.inputBackground} text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                        style={{ borderColor: commonStyles.accentColor + '40' }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        End Date
                      </label>
                      <input
                        type="date"
                        value={endDate || ''}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate || undefined}
                        className={`w-full px-3 py-2.5 rounded-lg border ${commonStyles.borderColor} ${commonStyles.inputBackground} text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                        style={{ borderColor: commonStyles.accentColor + '40' }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={startTime || ''}
                        onChange={(e) => setStartTime(e.target.value)}
                        step="1"
                        lang="en-GB"
                        className={`w-full px-3 py-2.5 rounded-lg border ${commonStyles.borderColor} ${commonStyles.inputBackground} text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                        style={{ borderColor: commonStyles.accentColor + '40' }}
                        // HTML5 time inputs always use 24-hour format internally (HH:MM)
                        // lang="en-GB" helps ensure 24-hour display format in most browsers
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        End Time
                      </label>
                      <input
                        type="time"
                        value={endTime || ''}
                        onChange={(e) => setEndTime(e.target.value)}
                        step="1"
                        lang="en-GB"
                        className={`w-full px-3 py-2.5 rounded-lg border ${commonStyles.borderColor} ${commonStyles.inputBackground} text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                        style={{ borderColor: commonStyles.accentColor + '40' }}
                        // HTML5 time inputs always use 24-hour format internally (HH:MM)
                        // lang="en-GB" helps ensure 24-hour display format in most browsers
                      />
                    </div>
                  </div>
                </div>
                {(startDate || endDate) && (
                  <div className="mt-2 flex items-center justify-between">
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {startDate && endDate 
                        ? `${startDate}${startTime ? ' ' + formatTimeDisplay(startTime) : ''} to ${endDate}${endTime ? ' ' + formatTimeDisplay(endTime) : ''}`
                        : startDate 
                        ? `Start: ${startDate}${startTime ? ' ' + formatTimeDisplay(startTime) : ''}`
                        : `End: ${endDate}${endTime ? ' ' + formatTimeDisplay(endTime) : ''}`
                      }
                    </span>
                    <button
                      onClick={() => {
                        setStartDate('');
                        setEndDate('');
                        setStartTime('');
                        setEndTime('');
                      }}
                      className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className={`relative flex justify-end px-4 md:px-6 py-3 md:py-4 border-t ${commonStyles.borderColor} transition-colors duration-300`}>
              <button
                onClick={closeFilterModal}
                className={`px-4 md:px-5 py-1.5 md:py-2 rounded-lg text-sm font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg active:scale-95`}
                style={{ backgroundColor: commonStyles.accentColor, outline: 'none' }}
              >
                APPLY
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopBar;